// storeSetting/productCardfunction.js - Fixed ReviewSection with proper API call management
import { useState, useEffect, useCallback, useRef, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from "react-toastify";
import { useTranslation } from 'react-i18next';
import {
  FaStar,
  FaRegStar,
  FaRegHeart,
  FaHeart,
  FaTimes,
} from "react-icons/fa";
import {
  MdVerified,
  MdInfo,
  MdZoomIn,
  MdChevronLeft as MdChevronLeftIcon,
  MdChevronRight as MdChevronRightIcon,
  MdInventory,
  MdColorLens,
  MdStraighten,
  MdSettings,
  MdCheck,
} from "react-icons/md";
import { AWS_URL } from "../../../services/http.service";
import { generateVariantName } from '../../../components/variant/varianthelper';
import {renderStars,formatDate} from '../../../helpers/csshelper';
// Import review actions
import {
  getProductReviewsData,
  getProductReviewStatsData,
  getCustomerProductReviewData,
  likeReviewData,
  openReviewImagePopup,
  closeReviewImagePopup,
  nextReviewImage,
  prevReviewImage,
  setReviewImageIndex,
} from '../../../store/actions/reviewActions';

export const ReviewSection = memo(function ReviewSection({
  product,
  bussinessUuid,
  customerProfile,
  theme,
  onReviewAdded,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  const loadedProductId = useRef(null);
  const loadedBusinessUuid = useRef(null);
  const loadedCustomerId = useRef(null);
  const isLoadingRef = useRef(false);
  
  const reviews = useSelector((state) => state.reviews?.reviews || []);
  const reviewStats = useSelector((state) => state.reviews?.reviewStats) || {
    averageRating: 0,
    totalReviews: 0,
    verifiedReviews: 0,
    withPhotos: 0,
    ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
  };
  const customerReview = useSelector((state) => state.reviews?.customerReview);
  const loading = useSelector((state) => state.reviews?.isLoading);
  const statsLoading = useSelector((state) => state.reviews?.statsLoading);
  const pagination = useSelector((state) => state.reviews?.pagination) || {
    total: 0,
    page: 1,
    pages: 1,
    limit: 5,
    hasMore: false
  };
  
  const imagePopup = useSelector((state) => state.reviews?.imagePopup) || {
    isOpen: false,
    images: [],
    currentIndex: 0,
  };
  
  const [activeTab, setActiveTab] = useState("all");
  const [page, setPage] = useState(1);
  const [expandedReview, setExpandedReview] = useState(null);
  const [likeLoading, setLikeLoading] = useState({});

  // Load initial data only once when product changes
  useEffect(() => {
    const currentProductId = product?.productId;
    const currentBussinessUuid = bussinessUuid;
    
    // Check if we need to load data (product changed or first load)
    const shouldLoadData = 
      currentProductId && 
      currentBussinessUuid && 
      (loadedProductId.current !== currentProductId || 
       loadedBusinessUuid.current !== currentBussinessUuid) &&
      !isLoadingRef.current;

    if (shouldLoadData) {
      
      isLoadingRef.current = true;
      
      // Load reviews and stats
      dispatch(getProductReviewsData(currentBussinessUuid, currentProductId, {
        page: 1,
        limit: 5,
        sort: "newest",
        filter: activeTab,
      }));
      
      dispatch(getProductReviewStatsData(currentBussinessUuid, currentProductId));
      
      // Update refs
      loadedProductId.current = currentProductId;
      loadedBusinessUuid.current = currentBussinessUuid;
      
      // Reset loading flag after a short delay
      setTimeout(() => {
        isLoadingRef.current = false;
      }, 1000);
    }
  }, [dispatch, product?.productId, bussinessUuid]); // Only depend on these

  // Load customer's review only when customer changes
  useEffect(() => {
    const currentCustomerId = customerProfile?.customerId;
    const currentProductId = product?.productId;
    const currentBussinessUuid = bussinessUuid;
    
    // Check if we need to load customer review
    const shouldLoadCustomerReview = 
      currentCustomerId && 
      !customerProfile?.isGuest && 
      currentProductId && 
      currentBussinessUuid &&
      loadedCustomerId.current !== currentCustomerId;

    if (shouldLoadCustomerReview) {
      dispatch(getCustomerProductReviewData(currentBussinessUuid, currentProductId, currentCustomerId));
      loadedCustomerId.current = currentCustomerId;
    }
  }, [dispatch, customerProfile, product?.productId, bussinessUuid]);

  // Handle tab change - only load when tab changes
  const handleTabChange = useCallback((tab) => {
    if (tab === activeTab || !bussinessUuid || !product?.productId) return;
    
    setActiveTab(tab);
    setPage(1);
    
    // Load data for new tab
    dispatch(getProductReviewsData(bussinessUuid, product.productId, {
      page: 1,
      limit: 5,
      sort: "newest",
      filter: tab,
    }));
  }, [activeTab, dispatch, bussinessUuid, product?.productId]);

  // Handle page change for load more
  const loadMoreReviews = useCallback(() => {
    if (loading || !bussinessUuid || !product?.productId) return;
    
    const nextPage = page + 1;
    setPage(nextPage);
    
    dispatch(getProductReviewsData(bussinessUuid, product.productId, {
      page: nextPage,
      limit: 5,
      sort: "newest",
      filter: activeTab,
    }));
  }, [dispatch, bussinessUuid, product?.productId, activeTab, page, loading]);

  // Handle new review added - refresh data
  useEffect(() => {
    if (onReviewAdded && loadedProductId.current === product?.productId) {
      const timer = setTimeout(() => {
        if (bussinessUuid && product?.productId) {
          dispatch(getProductReviewsData(bussinessUuid, product.productId, {
            page: 1,
            limit: 5,
            sort: "newest",
            filter: activeTab,
          }));
          dispatch(getProductReviewStatsData(bussinessUuid, product.productId));
          
          // Refresh customer review if logged in
          const customerId =  customerProfile?.customerId ;
          if (customerId && !customerProfile?.isGuest) {
            dispatch(getCustomerProductReviewData(bussinessUuid, product.productId, customerId));
          }
        }
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [dispatch, onReviewAdded, bussinessUuid, product?.productId, activeTab, customerProfile]);

  const toggleReviewExpand = useCallback((reviewId) => {
    setExpandedReview(prev => prev === reviewId ? null : reviewId);
  }, []);
  
  const handleLikeReview = useCallback(async (reviewId) => {
    const savedUser = localStorage.getItem(`user_${bussinessUuid}`);
    
    if (!savedUser) {
      toast.error(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.LOGIN_REQUIRED_LIKE"));
      return;
    }
    
    let customerId = null;
    try {
      const userData = JSON.parse(savedUser);
      customerId = userData.customerId || userData._id || userData.id;
    
    } catch (e) {
      console.error('Error parsing user data:', e);
      toast.error(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.ERROR_READING_USER"));
      return;
    }
    
    if (!customerId) {
      toast.error(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.CUSTOMER_ID_NOT_FOUND"));
      return;
    }

    setLikeLoading((prev) => ({ ...prev, [reviewId]: true }));

    try {
      const result = await dispatch(likeReviewData(bussinessUuid, reviewId, customerId));
      
      if (result?.success === false) {
        toast.error(result.error || t("STOREPRODUCT.REVIEW_SECTION.LIKE_FAILED"));
      }
    } catch (error) {
      console.error("Error liking review:", error);
      toast.error(error.message || t("STOREPRODUCT.REVIEW_SECTION.LIKE_FAILED"));
    } finally {
      setLikeLoading((prev) => ({ ...prev, [reviewId]: false }));
    }
  }, [dispatch, bussinessUuid, t]);
  
  const renderRatingBar = useCallback((rating, count, total) => {
    const percentage = total > 0 ? (count / total) * 100 : 0;
    return (
      <div className='flex items-center gap-2 w-full'>
        <span className='text-xs text-gray-600 w-8'>{rating}★</span>
        <div className='flex-1 h-2 bg-gray-200 rounded-full overflow-hidden'>
          <div
            className='h-full'
            style={{
              width: `${percentage}%`,
              backgroundColor: theme?.primaryColor || "#4f46e5",
            }}
          ></div>
        </div>
        <span className='text-xs text-gray-600 w-12'>{count} </span>
      </div>
    );
  }, [theme?.primaryColor, t]);


const isReviewLikedByCustomer = useCallback((review) => {
  if (!customerProfile || !review.likes) return false;
  
  // Try multiple possible ID fields
  const customerId = customerProfile.customerId || 
                     customerProfile.userId;
  
  if (!customerId) return false;
  
  // Convert both to string for comparison
  const customerIdStr = customerId.toString();
  return review.likes.some((id) => id.toString() === customerIdStr);
}, [customerProfile]);

  // Image popup handlers
  const handleOpenImagePopup = useCallback((images, startIndex = 0) => {
    dispatch(openReviewImagePopup(images, startIndex));
  }, [dispatch]);

  const handleCloseImagePopup = useCallback(() => {
    dispatch(closeReviewImagePopup());
  }, [dispatch]);

  const handleNextImage = useCallback((e) => {
    if (e) e.stopPropagation();
    dispatch(nextReviewImage());
  }, [dispatch]);

  const handlePrevImage = useCallback((e) => {
    if (e) e.stopPropagation();
    dispatch(prevReviewImage());
  }, [dispatch]);

  // Function to get full image URL
  const getFullImageUrl = useCallback((photo) => {
    const photoUrl =
      typeof photo === "string" ? photo : photo?.url || photo?.photoUrl || "";

    if (photoUrl.startsWith("http")) {
      return photoUrl;
    } else if (photoUrl.startsWith("/")) {
      return `${window.location.origin}${photoUrl}`;
    } else {
      return `${AWS_URL}${photoUrl}`;
    }
  }, []);

  // Function to get all image URLs from a review
  const getReviewImages = useCallback((review) => {
    if (!review.photos || review.photos.length === 0) return [];
    return review.photos.map((photo) => ({
      url: getFullImageUrl(photo),
      alt: review.productName || t("STOREPRODUCT.REVIEW_SECTION.REVIEW_IMAGE"),
    }));
  }, [getFullImageUrl, t]);

  // Don't show loading spinner on initial page load if we already have data
  if (loading && page === 1 && reviews.length === 0) {
    return (
      <div className='py-4 text-center'>
        <div
          className='animate-spin rounded-full h-8 w-8 border-b-2 mx-auto'
          style={{ borderColor: theme?.primaryColor || "#4f46e5" }}
        ></div>
      </div>
    );
  }

  return (
    <div className='py-6 border-t border-gray-200'>
      <h3
        className='text-xl font-bold mb-4'
        style={{ color: theme?.headingColor }}
      >
        {t("STOREPRODUCT.REVIEW_SECTION.TITLE")}
      </h3>

      {/* Review Stats */}
      <div className='bg-gray-50 rounded-lg p-4 mb-6'>
        <div className='flex flex-col md:flex-row gap-6'>
          <div className='flex flex-col items-center justify-center'>
            <div
              className='text-4xl font-bold mb-1'
              style={{ color: theme?.primaryColor }}
            >
              {reviewStats.averageRating?.toFixed(1) || "0.0"}
            </div>
            <div className='flex mb-2'>
              {renderStars(reviewStats.averageRating || 0)}
            </div>
            <div className='text-sm text-gray-600'>
              {reviewStats.totalReviews || 0} customer reviews
            </div>
            {reviewStats.verifiedReviews > 0 && (
              <div className='text-xs text-green-600 mt-1'>
                {reviewStats.verifiedReviews} verified purchases
              </div>
            )}
          </div>

          <div className='flex-1'>
            {[5, 4, 3, 2, 1].map((rating) => (
              <div key={rating} className='mb-1'>
                {renderRatingBar(
                  rating,
                  reviewStats.ratingDistribution?.[rating] || 0,
                  reviewStats.totalReviews || 0,
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Review Tabs */}
      <div className='flex flex-wrap gap-2 mb-4'>
        <button
          className={`px-3 py-1.5 text-sm rounded-full ${activeTab === "all" ? "text-white" : "text-gray-700 border"}`}
          onClick={() => handleTabChange("all")}
          style={{
            backgroundColor:
              activeTab === "all"
                ? theme?.primaryColor || "#4f46e5"
                : "transparent",
            borderColor:
              activeTab !== "all" ? `${theme?.primaryColor}30` : "transparent",
          }}
        >
         {t("STOREPRODUCT.REVIEW_SECTION.TABS.ALL")} ({reviewStats.totalReviews || 0 })
        </button>
        {/* {reviewStats.verifiedReviews > 0 && (
          <button
            className={`px-3 py-1.5 text-sm rounded-full ${activeTab === "verified" ? "text-white" : "text-gray-700 border"}`}
            onClick={() => handleTabChange("verified")}
            style={{
              backgroundColor:
                activeTab === "verified"
                  ? theme?.primaryColor || "#4f46e5"
                  : "transparent",
              borderColor:
                activeTab !== "verified"
                  ? `${theme?.primaryColor}30`
                  : "transparent",
            }}
          >
            {t("STOREPRODUCT.REVIEW_SECTION.TABS.VERIFIED", { count: reviewStats.verifiedReviews })}
          </button>
        )}
        {reviewStats.withPhotos > 0 && (
          <button
            className={`px-3 py-1.5 text-sm rounded-full ${activeTab === "photos" ? "text-white" : "text-gray-700 border"}`}
            onClick={() => handleTabChange("photos")}
            style={{
              backgroundColor:
                activeTab === "photos"
                  ? theme?.primaryColor || "#4f46e5"
                  : "transparent",
              borderColor:
                activeTab !== "photos"
                  ? `${theme?.primaryColor}30`
                  : "transparent",
            }}
          >
            {t("STOREPRODUCT.REVIEW_SECTION.TABS.WITH_PHOTOS", { count: reviewStats.withPhotos })}
          </button>
        )} */}
      </div>

      {/* Customer's Existing Review */}
      {customerReview && (
        <div className='bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-6 border border-blue-200 shadow-sm'>
          <div className='flex justify-between items-start mb-2'>
            <div>
              <div className='flex items-center gap-2 mb-1'>
                <h4 className='font-semibold'>{t("STOREPRODUCT.REVIEW_SECTION.YOUR_REVIEW")}</h4>
                {customerReview.isVerifiedPurchase && (
                  <span className='text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full flex items-center gap-1'>
                    <MdVerified size={12} />
                    {t("STOREPRODUCT.REVIEW_SECTION.VERIFIED_PURCHASE")}
                  </span>
                )}
              </div>
              <div className='flex items-center gap-2'>
                {renderStars(customerReview.rating, "text-sm")}
                <span className='text-xs text-gray-600'>
                  {t("STOREPRODUCT.REVIEW_SECTION.REVIEWED_ON", { date: formatDate(customerReview.createdAt) })}
                </span>
              </div>
            </div>
          </div>
          {customerReview.title && (
            <h5
              className='font-medium mb-2'
              style={{ color: theme?.headingColor }}
            >
              {customerReview.title}
            </h5>
          )}
          <p className='text-sm text-gray-700'>{customerReview.comment}</p>
          {customerReview.photos?.length > 0 && (
            <div className='flex flex-wrap gap-2 mt-3'>
              {customerReview.photos.map((photo, index) => {
                const fullUrl = getFullImageUrl(photo);
                const allImages = getReviewImages(customerReview);

                return (
                  <div key={index} className='relative group'>
                    <img
                      src={fullUrl}
                      alt={`${t("STOREPRODUCT.REVIEW_SECTION.YOUR_REVIEW")} ${index + 1}`}
                      className='w-28 h-28 md:w-32 md:h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-all duration-200 hover:scale-105 shadow-sm'
                      onClick={() => handleOpenImagePopup(allImages, index)}
                    />
                    <div
                      className='absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity rounded flex items-center justify-center cursor-pointer'
                      onClick={() => handleOpenImagePopup(allImages, index)}
                    >
                      <MdZoomIn
                        className='text-white opacity-0 group-hover:opacity-70 transition-opacity'
                        size={20}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Review Login Prompt */}
      {!customerProfile && (
        <div className='bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-6 border border-blue-200'>
          <div className='flex items-start gap-3'>
            <div className='p-2 rounded-full bg-blue-100'>
              <MdInfo className='text-blue-600' size={20} />
            </div>
            <div className='flex-1'>
              <h4 className='font-semibold mb-1'>
                {t("STOREPRODUCT.REVIEW_SECTION.WANT_TO_SHARE")}
              </h4>
              <p className='text-sm text-gray-700'>
                {t("STOREPRODUCT.REVIEW_SECTION.LOGIN_TO_REVIEW")}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Public Reviews List */}
      <div className='space-y-4'>
        {reviews.length > 0 ? (
          reviews.map((review) => {
            const isExpanded = expandedReview === review._id;
            const isLiked = isReviewLikedByCustomer(review);

            return (
              <div
                key={review._id}
                className='bg-white rounded-lg p-4 border border-gray-200 shadow-sm'
              >
                <div className='flex justify-between items-start mb-3'>
                  <div className='flex items-center gap-3'>
                    <div className='w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center overflow-hidden'>
                      <span
                        className='font-semibold text-sm'
                        style={{ color: theme?.primaryColor }}
                      >
                        {(review.customerName?.charAt(0) || "C").toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className='flex items-center gap-2'>
                        <span className='font-medium'>
                          {review.customerName || t("STOREPRODUCT.REVIEW_SECTION.ANONYMOUS")}
                        </span>
                        {review.isVerifiedPurchase && (
                          <MdVerified
                            className='text-green-500'
                            title={t("STOREPRODUCT.REVIEW_SECTION.VERIFIED_PURCHASE")}
                          />
                        )}
                      </div>
                      <div className='flex items-center gap-2 mt-1'>
                        {renderStars(review.rating, "text-sm")}
                        <span className='text-xs text-gray-600'>
                          {formatDate(review.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className='flex gap-2'>
                  <button
  onClick={() => handleLikeReview(review._id)}
  disabled={likeLoading[review._id]} // Only disable when loading
  className='flex items-center gap-1 text-xs text-gray-600 hover:text-red-500 disabled:opacity-50 disabled:cursor-not-allowed'
>
  {likeLoading[review._id] ? (
    <div className='animate-spin rounded-full h-3 w-3 border-b-2 border-current'></div>
  ) : isLiked ? (
    <FaHeart className='text-red-500' />
  ) : (
    <FaRegHeart />
  )}
  <span>{review.likesCount || 0}</span>
</button>
                  </div>
                </div>

                {review.title && (
                  <h5
                    className='font-medium mb-2'
                    style={{ color: theme?.headingColor }}
                  >
                    {review.title}
                  </h5>
                )}

                <div className='relative'>
                  <p
                    className={`text-sm text-gray-700 mb-3 ${!isExpanded && review.comment?.length > 200 ? "line-clamp-3" : ""}`}
                  >
                    {review.comment}
                  </p>
                  {review.comment?.length > 200 && (
                    <button
                      onClick={() => toggleReviewExpand(review._id)}
                      className='text-xs font-medium'
                      style={{ color: theme?.primaryColor || "#4f46e5" }}
                    >
                      {isExpanded ? t("STOREPRODUCT.REVIEW_SECTION.SHOW_LESS") : t("STOREPRODUCT.REVIEW_SECTION.READ_MORE")}
                    </button>
                  )}
                </div>

                {review.photos?.length > 0 && (
                  <div className='flex flex-wrap gap-2 mt-3'>
                    {review.photos.map((photo, index) => {
                      const fullUrl = getFullImageUrl(photo);
                      const allImages = getReviewImages(review);

                      return (
                        <div key={index} className='relative group'>
                          <img
                            src={fullUrl}
                            alt={`${t("STOREPRODUCT.REVIEW_SECTION.REVIEW_IMAGE")} ${index + 1}`}
                            className='w-28 h-28 md:w-32 md:h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-all duration-200 hover:scale-105 shadow-sm'
                            onClick={() => handleOpenImagePopup(allImages, index)}
                          />
                          <div
                            className='absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity rounded flex items-center justify-center cursor-pointer'
                            onClick={() => handleOpenImagePopup(allImages, index)}
                          >
                            <MdZoomIn
                              className='text-white opacity-0 group-hover:opacity-70 transition-opacity'
                              size={20}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className='text-center py-8 text-gray-500'>
            {reviewStats.totalReviews === 0 ? (
              <>
                <div className='text-4xl mb-3'>📝</div>
                <p className='font-medium'>{t("STOREPRODUCT.REVIEW_SECTION.NO_REVIEWS_YET")}</p>
                <p className='text-sm mt-1'>
                  {t("STOREPRODUCT.REVIEW_SECTION.BE_FIRST_TO_REVIEW")}
                </p>
              </>
            ) : (
              <p>{t("STOREPRODUCT.REVIEW_SECTION.NO_REVIEWS_MATCH")}</p>
            )}
          </div>
        )}
      </div>

      {/* Load More Button */}
      {pagination.hasMore !== false && reviews.length > 0 && reviews.length < (reviewStats.totalReviews || 0) && (
        <div className='mt-6 text-center'>
          <button
            onClick={loadMoreReviews}
            disabled={loading}
            className='px-4 py-2 rounded-lg border font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            style={{
              borderColor: theme?.primaryColor || "#4f46e5",
              color: theme?.primaryColor || "#4f46e5",
              borderRadius: theme?.buttonStyle === "pill" ? "9999px" : "8px",
            }}
          >
            {loading ? t("STOREPRODUCT.REVIEW_SECTION.LOADING") : t("STOREPRODUCT.REVIEW_SECTION.LOAD_MORE")}
          </button>
        </div>
      )}

      {/* Image Popup Modal */}
      {imagePopup.isOpen && imagePopup.images.length > 0 && (
        <div
          className='fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-95 backdrop-blur-sm transition-all duration-300'
          onClick={handleCloseImagePopup}
        >
          <button
            onClick={handleCloseImagePopup}
            className='absolute top-6 right-6 z-10 p-3 rounded-full bg-white bg-opacity-10 hover:bg-opacity-20 transition-colors hover:scale-110 border border-white border-opacity-20'
            style={{ borderRadius: "50%" }}
          >
            <FaTimes className='h-6 w-6 text-white' />
          </button>

          {imagePopup.images.length > 1 && (
            <>
              <button
                onClick={handlePrevImage}
                className='absolute left-6 top-1/2 transform -translate-y-1/2 z-10 p-3 rounded-full bg-white bg-opacity-10 hover:bg-opacity-20 transition-colors hover:scale-110 border border-white border-opacity-20'
                style={{ borderRadius: "50%" }}
              >
                <MdChevronLeftIcon className='h-8 w-8 text-white' />
              </button>

              <button
                onClick={handleNextImage}
                className='absolute right-6 top-1/2 transform -translate-y-1/2 z-10 p-3 rounded-full bg-white bg-opacity-10 hover:bg-opacity-20 transition-colors hover:scale-110 border border-white border-opacity-20'
                style={{ borderRadius: "50%" }}
              >
                <MdChevronRightIcon className='h-8 w-8 text-white' />
              </button>
            </>
          )}

          <div
            className='relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='relative w-full h-full flex flex-col items-center justify-center'>
              <img
                src={imagePopup.images[imagePopup.currentIndex].url}
                alt={imagePopup.images[imagePopup.currentIndex].alt}
                className='max-w-full max-h-[70vh] w-auto h-auto object-contain rounded-lg shadow-2xl'
              />

              {imagePopup.images.length > 1 && (
                <div className='mt-4 text-white text-center'>
                  <div className='text-sm opacity-80'>
                    {imagePopup.currentIndex + 1} / {imagePopup.images.length}
                  </div>
                  <div className='flex items-center justify-center space-x-2 mt-2'>
                    {imagePopup.images.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={(e) => {
                          e.stopPropagation();
                          dispatch(setReviewImageIndex(idx));
                        }}
                        className={`w-2 h-2 rounded-full transition-all ${
                          idx === imagePopup.currentIndex
                            ? "bg-white scale-125"
                            : "bg-white bg-opacity-50 hover:bg-opacity-75"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className='mt-4 text-white text-center text-sm opacity-80'>
                {t("STOREPRODUCT.REVIEW_SECTION.IMAGE_POPUP.CLOSE")}
              </div>
            </div>
          </div>

          {/* Thumbnails for desktop */}
          {imagePopup.images.length > 1 && (
            <div className='absolute bottom-6 left-0 right-0 overflow-x-auto py-4'>
              <div className='flex items-center justify-center space-x-3 px-6'>
                {imagePopup.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(setReviewImageIndex(idx));
                    }}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      idx === imagePopup.currentIndex
                        ? "border-white scale-110"
                        : "border-transparent hover:border-white hover:border-opacity-50"
                    }`}
                  >
                    <img
                      src={img.url}
                      alt={`${t("STOREPRODUCT.REVIEW_SECTION.THUMBNAIL")} ${idx + 1}`}
                      className='w-full h-full object-cover'
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export function VariantSelector({ product, selectedVariant, onVariantSelect, theme }) {
  const { t } = useTranslation();
  const [expandedSections, setExpandedSections] = useState({});

  if (!product.hasVariants || !product.variants || product.variants.length === 0) {
    return null;
  }

  const themeStyles = {
    primaryColor: theme?.primaryColor || "#4f46e5",
    buttonColor: theme?.buttonColor || "#4f46e5",
    textColor: theme?.textColor || "#1f2937",
    headingColor: theme?.headingColor || "#111827",
    fontFamily: theme?.fontFamily || "Inter, sans-serif",
    borderRadius: theme?.borderRadius || "8px",
    buttonStyle: theme?.buttonStyle || "rounded",
  };

  const getButtonBorderRadius = () => {
    switch (themeStyles.buttonStyle) {
      case "pill":
        return "9999px";
      case "square":
        return "4px";
      case "rounded":
      default:
        return themeStyles.borderRadius;
    }
  };

  // Group variants by attributes
  const getAttributeGroups = () => {
    const groups = {};

    product.variants.forEach((variant) => {
      if (variant.attributes && Array.isArray(variant.attributes)) {
        variant.attributes.forEach((attr) => {
          if (!groups[attr.type]) {
            groups[attr.type] = {
              values: new Set(),
              variants: [],
            };
          }
          groups[attr.type].values.add(attr.value);
          groups[attr.type].variants.push(variant);
        });
      }
    });

    Object.keys(groups).forEach((key) => {
      groups[key].values = Array.from(groups[key].values).sort();
    });

    return groups;
  };

  // Get all unique attribute types
  const getAttributeTypes = () => {
    const types = new Set();
    product.variants.forEach((variant) => {
      if (variant.attributes && Array.isArray(variant.attributes)) {
        variant.attributes.forEach((attr) => {
          types.add(attr.type);
        });
      }
    });
    return Array.from(types);
  };

  // Get icon for attribute type
  const getAttributeIcon = (type) => {
    switch (type) {
      case "color":
        return <MdColorLens className='h-4 w-4 mr-2' />;
      case "size":
        return <MdStraighten className='h-4 w-4 mr-2' />;
      case "model":
        return <MdSettings className='h-4 w-4 mr-2' />;
      default:
        return <MdSettings className='h-4 w-4 mr-2' />;
    }
  };

  // Find variant by selected attributes
  const findVariantByAttributes = (attributes) => {
    if (!attributes || !Array.isArray(attributes)) return null;

    return product.variants.find((variant) => {
      if (!variant.attributes || !Array.isArray(variant.attributes))
        return false;

      return (
        attributes.every((selectedAttr) =>
          variant.attributes.some(
            (variantAttr) =>
              variantAttr.type === selectedAttr.type &&
              variantAttr.value === selectedAttr.value,
          ),
        ) && variant.attributes.length === attributes.length
      );
    });
  };

  // Handle attribute selection
  const handleAttributeSelect = (attributeType, attributeValue) => {
    let currentAttributes = selectedVariant?.attributes
      ? [...selectedVariant.attributes]
      : [];

    currentAttributes = currentAttributes.filter(
      (attr) => attr.type !== attributeType,
    );

    currentAttributes.push({
      type: attributeType,
      value: attributeValue,
    });

    const matchingVariant = findVariantByAttributes(currentAttributes);

    if (matchingVariant) {
      onVariantSelect(matchingVariant);
    } else {
      const tempVariant = {
        ...product,
        variantName: generateVariantName(
          currentAttributes,
          product.variantType,
        ),
        attributes: currentAttributes,
        quantity: 0,
        salePrice: product.salePrice || "0",
        purchasePrice: product.purchasePrice || "0",
        mrp: product.mrp || "0",
        isTemporary: true,
      };
      onVariantSelect(tempVariant);
    }
  };

  const attributeGroups = getAttributeGroups();
  const attributeTypes = getAttributeTypes();

  const toggleSection = (type) => {
    setExpandedSections((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  // Render color swatch
  const renderColorSwatch = (color, isSelected, onClick) => {
    return (
      <button
        onClick={onClick}
        className={`w-8 h-8 rounded-full border flex items-center justify-center ${isSelected ? "ring-1 ring-offset-1" : ""}`}
        style={{
          backgroundColor: color.toLowerCase(),
          borderColor: isSelected ? themeStyles.primaryColor : "#d1d5db",
          boxShadow: isSelected
            ? `0 0 0 1px ${themeStyles.primaryColor}`
            : "none",
        }}
        title={color}
      >
        {isSelected && <MdCheck className='text-white' size={12} />}
      </button>
    );
  };

  // Render size button
  const renderSizeButton = (size, isSelected, onClick) => {
    return (
      <button
        onClick={onClick}
        className={`px-3 py-1.5 rounded border text-xs font-medium transition-all ${isSelected ? "border-2" : "border hover:border-gray-400"}`}
        style={{
          backgroundColor: isSelected
            ? `${themeStyles.primaryColor}10`
            : "white",
          borderColor: isSelected ? themeStyles.primaryColor : "#e5e7eb",
          color: isSelected ? themeStyles.primaryColor : themeStyles.textColor,
          borderRadius: getButtonBorderRadius(),
        }}
      >
        {size}
        {isSelected && <MdCheck className='inline ml-1 h-3 w-3' />}
      </button>
    );
  };

  return (
    <div className='w-full space-y-4'>
      {attributeTypes.map((type) => {
        const isExpanded = expandedSections[type] !== false;
        const values = attributeGroups[type]?.values || [];

        return (
          <div key={type} className='border border-gray-200 rounded-lg p-3'>
            <button
              onClick={() => toggleSection(type)}
              className='w-full flex items-center justify-between mb-2'
            >
              <div className='flex items-center'>
                {getAttributeIcon(type)}
                <span className='font-medium text-sm text-gray-800'>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </span>
                <span className='ml-2 text-xs text-gray-600'>
                  ({values.length})
                </span>
              </div>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className={`h-3 w-3 text-gray-500 transition-transform ${isExpanded ? "transform rotate-180" : ""}`}
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
              </svg>
            </button>

            {isExpanded && (
              <div className='mt-2'>
                {type === "color" ? (
                  <div className='flex flex-wrap gap-2'>
                    {values.map((value, index) => {
                      const isSelected = selectedVariant?.attributes?.some(
                        (attr) => attr.type === type && attr.value === value,
                      );

                      return (
                        <div key={index} className='flex flex-col items-center'>
                          {renderColorSwatch(value, isSelected, () =>
                            handleAttributeSelect(type, value),
                          )}
                          <span className='text-xs mt-0.5 text-gray-600'>
                            {value}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className='flex flex-wrap gap-1.5'>
                    {values.map((value, index) => {
                      const isSelected = selectedVariant?.attributes?.some(
                        (attr) => attr.type === type && attr.value === value,
                      );

                      return (
                        <div key={index}>
                          {renderSizeButton(value, isSelected, () =>
                            handleAttributeSelect(type, value),
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Selected Variant Summary */}
      {selectedVariant && selectedVariant.variantName && (
        <div className='bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-200'>
          <div className='flex justify-between items-center'>
            <div>
              <div className='font-medium text-sm text-gray-800'>
                {selectedVariant.variantName}
              </div>
              <div className='text-xs text-gray-600 mt-0.5'>
                {selectedVariant.quantity > 0 ? (
                  <span className='text-green-600 flex items-center'>
                    <MdInventory className='inline h-3 w-3 mr-1' />
                    {selectedVariant.quantity} in stock
                  </span>
                ) : (
                  <span className='text-red-600'>Out of stock</span>
                )}
              </div>
            </div>
            {selectedVariant.salePrice && (
              <div
                className='text-base font-bold'
                style={{ color: themeStyles.primaryColor }}
              >
                ₹{selectedVariant.salePrice}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}